import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { COMPANY, NAV_LINKS } from "../data/siteData";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Close dropdown / mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Track scroll for subtle header background enhancement
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setServicesOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isServiceActive = location.pathname.startsWith("/ydelser");

  return (
    <header className="sticky top-0 z-40 w-full">
      {/* Top Utility Micro-Bar */}
      <div className="bg-[#050912] border-b border-slate-800/80 text-xs py-2 px-4 sm:px-6 lg:px-8 text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-[11px] sm:text-xs">
            <span className="inline-flex items-center gap-1.5 text-[#2DD4BF] font-mono">
              <span className="w-1.5 h-1.5 rounded-full bg-[#2DD4BF] animate-pulse"></span>
              Køle- & medicinaltransport
            </span>
            <span className="hidden md:inline-block text-slate-600">|</span>
            <span className="hidden md:inline-flex items-center gap-1.5 text-slate-300">
              <i className="fa-solid fa-location-dot text-[#2DD4BF] text-[10px]" aria-hidden="true" />
              {COMPANY.location} · {COMPANY.coverageShort}
            </span>
          </div>

          <div className="flex items-center gap-5 text-[11px] sm:text-xs ml-auto">
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              className="inline-flex items-center gap-1.5 hover:text-white transition whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-[#2DD4BF] text-[10px]" aria-hidden="true" />
              <span className="whitespace-nowrap font-medium text-slate-300">{COMPANY.phone}</span>
            </a>
            <a
              href={`mailto:${COMPANY.email}`}
              className="inline-flex items-center gap-1.5 hover:text-white transition whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope text-[#2DD4BF] text-[10px]" aria-hidden="true" />
              <span className="whitespace-nowrap font-medium text-slate-300">{COMPANY.email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div
        className={`transition-all duration-200 border-b ${
          scrolled
            ? "bg-[#070d18]/95 backdrop-blur-md border-slate-800 shadow-xl"
            : "bg-[#070d18] border-slate-800/80"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Brand Logo */}
            <Link to="/" className="flex items-center group py-1" aria-label="KVC Express ApS - Forside">
              <img
                src="https://assets.auxodev.dk/assets/ac8ce1b6-e4be-4439-9ce6-53c89a29cb07/kvc-nyt-logo-2.png"
                alt="KVC Express ApS Logo"
                className="h-12 sm:h-14 md:h-16 w-auto max-w-[280px] sm:max-w-[340px] md:max-w-[380px] object-contain transition-transform duration-200 group-hover:scale-[1.02]"
              />
            </Link>

            {/* Desktop Navigation - Strictly displayed at min-width 1200px */}
            <nav className="hidden min-[1200px]:flex items-center gap-8">
              {/* Ydelser Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setServicesOpen(!servicesOpen)}
                  onMouseEnter={() => setServicesOpen(true)}
                  className={`flex items-center gap-2 text-sm font-semibold tracking-wide transition py-2 cursor-pointer ${
                    isServiceActive
                      ? "text-[#2DD4BF]"
                      : "text-slate-300 hover:text-white"
                  }`}
                  aria-expanded={servicesOpen}
                >
                  <span>Ydelser</span>
                  <i
                    className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${
                      servicesOpen ? "rotate-180 text-[#2DD4BF]" : "text-slate-500"
                    }`}
                    aria-hidden="true"
                  />
                </button>

                {/* Dropdown Menu */}
                {servicesOpen && (
                  <div
                    onMouseLeave={() => setServicesOpen(false)}
                    className="absolute top-full left-0 w-80 bg-[#0b1426] border border-slate-700/80 rounded-xl shadow-2xl p-2 z-50 animate-in fade-in slide-in-from-top-2 duration-150"
                  >
                    <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-slate-400 border-b border-slate-800">
                      Specialiserede B2B Ydelser
                    </div>
                    <div className="mt-1 space-y-1">
                      {NAV_LINKS[0].dropdown.map((item) => (
                        <NavLink
                          key={item.href}
                          to={item.href}
                          onClick={() => setServicesOpen(false)}
                          className={({ isActive }) =>
                            `flex flex-col p-3 rounded-lg transition ${
                              isActive
                                ? "bg-[#0F766E]/20 text-[#2DD4BF] border border-[#2DD4BF]/30"
                                : "text-slate-300 hover:bg-slate-800/80 hover:text-white"
                            }`
                          }
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-sm">{item.title}</span>
                            <span className="text-[9px] font-mono uppercase tracking-wider bg-slate-900 px-2 py-0.5 rounded text-[#2DD4BF] border border-slate-800">
                              {item.tag}
                            </span>
                          </div>
                          <span className="text-xs text-slate-400 mt-1 leading-snug">
                            {item.desc}
                          </span>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Other Main Links */}
              {NAV_LINKS.slice(1).map((link) => (
                <NavLink
                  key={link.href}
                  to={link.href}
                  className={({ isActive }) =>
                    `text-sm font-semibold tracking-wide transition relative py-2 ${
                      isActive
                        ? "text-[#2DD4BF] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-[#2DD4BF]"
                        : "text-slate-300 hover:text-white"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>

            {/* Desktop CTA Button */}
            <div className="hidden min-[1200px]:flex items-center gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0F766E] hover:bg-[#115E59] active:bg-[#134E4A] text-white text-xs uppercase tracking-wider font-bold transition shadow-lg shadow-[#0F766E]/30 cursor-pointer"
              >
                <span>Drøft et samarbejde</span>
                <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
              </Link>
            </div>

            {/* Burger Menu Button - Appears strictly below 1200px */}
            <div className="flex min-[1200px]:hidden items-center gap-3">
              <Link
                to="/kontakt"
                className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md bg-[#0F766E] hover:bg-[#115E59] text-white text-xs font-semibold uppercase tracking-wider transition"
              >
                <span>Kontakt</span>
              </Link>
              <button
                type="button"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Åbn navigationsmenu"
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-slate-900 border border-slate-800 text-slate-200 hover:text-white hover:border-slate-700 transition cursor-pointer"
              >
                <i
                  className={`fa-solid ${mobileOpen ? "fa-xmark" : "fa-bars"} text-base`}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Drawer (visible on < 1200px when mobileOpen) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 min-[1200px]:hidden">
          {/* Backdrop overlay */}
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-xs transition-opacity animate-in fade-in duration-200"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />

          {/* Right-aligned Sidebar Drawer */}
          <aside
            aria-label="Mobil navigation"
            className="fixed top-0 right-0 bottom-0 w-[min(90vw,380px)] bg-[#070d18] border-l border-slate-800 shadow-2xl z-50 flex flex-col h-full animate-in slide-in-from-right duration-300"
          >
            {/* Sidebar Top Bar with Logo & Close Button */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800/80 bg-[#050912]">
              <div className="flex items-center gap-2.5">
                <img
                  src="https://assets.auxodev.dk/assets/ac8ce1b6-e4be-4439-9ce6-53c89a29cb07/kvc-nyt-logo-2.png"
                  alt="KVC Express ApS Logo"
                  className="h-8 w-auto max-w-[200px] object-contain"
                />
              </div>

              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="Luk navigationsmenu"
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-slate-900 border border-slate-800 text-slate-300 hover:text-white hover:border-slate-700 transition cursor-pointer"
              >
                <i className="fa-solid fa-xmark text-sm" aria-hidden="true" />
              </button>
            </div>

            {/* Scrollable Sidebar Body */}
            <div className="flex-1 overflow-y-auto overscroll-contain p-5 space-y-4">
              {/* Status badge */}
              <div className="px-3.5 py-2 rounded-lg bg-[#0F766E]/20 border border-[#2DD4BF]/30 text-[#2DD4BF] text-xs font-mono flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2DD4BF] animate-pulse"></span>
                <span>Køle- & medicinaltransport</span>
              </div>

              {/* Mobile Ydelser Accordion */}
              <div className="border border-slate-800 rounded-xl bg-[#0b1426] overflow-hidden">
                <button
                  type="button"
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="w-full flex items-center justify-between p-3.5 text-left font-semibold text-white text-sm cursor-pointer"
                >
                  <span className="flex items-center gap-2.5">
                    <i className="fa-solid fa-boxes-stacked text-[#2DD4BF] text-xs" aria-hidden="true" />
                    Ydelser
                  </span>
                  <i
                    className={`fa-solid fa-chevron-down text-xs text-slate-400 transition-transform ${
                      mobileServicesOpen ? "rotate-180 text-[#2DD4BF]" : ""
                    }`}
                    aria-hidden="true"
                  />
                </button>

                {mobileServicesOpen && (
                  <div className="px-3 pb-3 space-y-1 border-t border-slate-800/80 pt-2">
                    {NAV_LINKS[0].dropdown.map((sub) => (
                      <NavLink
                        key={sub.href}
                        to={sub.href}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                          `block px-3 py-2 rounded-lg text-xs font-medium transition ${
                            isActive
                              ? "bg-[#0F766E] text-white font-semibold"
                              : "text-slate-300 hover:bg-slate-800 hover:text-white"
                          }`
                        }
                      >
                        {sub.title}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Other Links */}
              <div className="space-y-1.5">
                {NAV_LINKS.slice(1).map((link) => (
                  <NavLink
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `block px-4 py-3 rounded-xl text-sm font-semibold transition border ${
                        isActive
                          ? "bg-[#0F766E]/20 text-[#2DD4BF] border-[#2DD4BF]/40"
                          : "bg-[#0b1426] text-slate-300 border-slate-800 hover:text-white hover:border-slate-700"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>

              {/* Mobile CTA */}
              <div className="pt-2">
                <Link
                  to="/kontakt"
                  onClick={() => setMobileOpen(false)}
                  className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl bg-[#0F766E] hover:bg-[#115E59] text-white text-xs uppercase tracking-wider font-bold transition shadow-lg shadow-[#0F766E]/30"
                >
                  <span>Drøft et samarbejde</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>

              {/* Mobile Contact Quick Details */}
              <div className="pt-4 border-t border-slate-800/80 text-xs text-slate-400 flex flex-col gap-2.5">
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 font-semibold">
                  Direkte kontakt
                </span>
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  className="flex items-center gap-2.5 text-slate-300 hover:text-white transition"
                >
                  <i className="fa-solid fa-phone text-[#2DD4BF] text-xs" aria-hidden="true" />
                  <span className="whitespace-nowrap font-medium">{COMPANY.phone}</span>
                </a>
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="flex items-center gap-2.5 text-slate-300 hover:text-white transition"
                >
                  <i className="fa-solid fa-envelope text-[#2DD4BF] text-xs" aria-hidden="true" />
                  <span className="whitespace-nowrap font-medium">{COMPANY.email}</span>
                </a>
                <div className="flex items-start gap-2.5 text-slate-300 text-[11px] pt-1">
                  <i className="fa-solid fa-location-dot text-[#2DD4BF] text-xs mt-0.5" aria-hidden="true" />
                  <span>
                    {COMPANY.location} · {COMPANY.coverageShort}
                  </span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </header>
  );
}
